/**
 * Locale-aware formatting utilities.
 * All functions accept an optional locale string (e.g. 'en-GB', 'de-DE').
 * Falls back to the browser's locale if none provided.
 */

// Map our language codes to BCP-47 locale tags used by Intl
const LOCALE_MAP = {
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  'en-AU': 'en-AU',
  'en-CA': 'en-CA',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-CA',
  'de-DE': 'de-DE',
  'es-ES': 'es-ES',
  'it-IT': 'it-IT',
  'nl-NL': 'nl-NL',
};

// Default currencies per locale
export const LOCALE_CURRENCY = {
  'en-GB': 'GBP',
  'en-US': 'USD',
  'en-AU': 'AUD',
  'en-CA': 'CAD',
  'fr-FR': 'EUR',
  'fr-CA': 'CAD',
  'de-DE': 'EUR',
  'es-ES': 'EUR',
  'it-IT': 'EUR',
  'nl-NL': 'EUR',
};

// Default date formats per locale (for display hints, actual formatting uses Intl)
export const LOCALE_DATE_FORMAT = {
  'en-GB': 'DD/MM/YYYY',
  'en-US': 'MM/DD/YYYY',
  'en-AU': 'DD/MM/YYYY',
  'en-CA': 'DD/MM/YYYY',
  'fr-FR': 'DD/MM/YYYY',
  'fr-CA': 'DD/MM/YYYY',
  'de-DE': 'DD.MM.YYYY',
  'es-ES': 'DD/MM/YYYY',
  'it-IT': 'DD/MM/YYYY',
  'nl-NL': 'DD-MM-YYYY',
};

function resolveLocale(lang) {
  return LOCALE_MAP[lang] || lang || 'en-GB';
}

/**
 * Format a date value using the user's locale.
 * @param {Date|string|number} date
 * @param {string} lang - language code e.g. 'en-GB'
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatDate(date, lang, options = {}) {
  if (!date) return '';
  const locale = resolveLocale(lang);
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const defaults = { day: 'numeric', month: 'short', year: 'numeric' };
  try {
    return new Intl.DateTimeFormat(locale, { ...defaults, ...options }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

/**
 * Format a date as a short numeric string (e.g. 01/03/2026 or 03/01/2026).
 */
export function formatDateNumeric(date, lang) {
  if (!date) return '';
  const locale = resolveLocale(lang);
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

/**
 * Format a time value using the user's locale.
 */
export function formatTime(date, lang, options = {}) {
  if (!date) return '';
  const locale = resolveLocale(lang);
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const defaults = { hour: '2-digit', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(locale, { ...defaults, ...options }).format(d);
  } catch {
    return d.toLocaleTimeString();
  }
}

/**
 * Format a date + time together.
 */
export function formatDateTime(date, lang, options = {}) {
  if (!date) return '';
  const locale = resolveLocale(lang);
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const defaults = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(locale, { ...defaults, ...options }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * Format a currency amount using the user's locale and preferred currency.
 * @param {number} amount
 * @param {string} lang - language code
 * @param {string} [currency] - override currency (e.g. 'EUR'). Defaults to locale default.
 */
export function formatCurrency(amount, lang, currency) {
  if (amount == null || isNaN(Number(amount))) return '';
  const locale = resolveLocale(lang);
  const curr = currency || LOCALE_CURRENCY[lang] || 'GBP';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: curr }).format(Number(amount));
  } catch {
    return `${curr} ${Number(amount).toFixed(2)}`;
  }
}

/**
 * Format a plain number with locale-appropriate decimal/thousands separators.
 */
export function formatNumber(value, lang, options = {}) {
  if (value == null || isNaN(Number(value))) return '';
  const locale = resolveLocale(lang);
  try {
    return new Intl.NumberFormat(locale, options).format(Number(value));
  } catch {
    return String(value);
  }
}

/**
 * Returns the default currency code for a given language.
 */
export function defaultCurrency(lang) {
  return LOCALE_CURRENCY[lang] || 'GBP';
}

/**
 * Returns the default date format string for a given language.
 */
export function defaultDateFormat(lang) {
  return LOCALE_DATE_FORMAT[lang] || 'DD/MM/YYYY';
}
